import * as fs from "node:fs";
import * as os from "node:os";
import * as path from "node:path";
import { randomBytes } from "node:crypto";
import * as readline from "node:readline/promises";
import { Command, Option } from "clipanion";
import { M8tCommand } from "../../lib/m8t-command.js";
import { createGitHubApp } from "../../lib/github-app-create.js";
import { signAppJwt } from "@m8t-stack/github-app-auth";
import { findValidBrainApp } from "../../lib/github-app-existing.js";
import { tryOpenUrl } from "../../lib/gh-helpers.js";
import { colors } from "../../lib/output.js";
import { LocalCliError } from "../../lib/errors.js";

/** Interactive keep-vs-new prompt (only called when stdout is a TTY). */
async function promptKeepNew(
  ctx: { stdin: NodeJS.ReadStream; stdout: NodeJS.WriteStream },
  slug: string,
  org: string,
): Promise<boolean> {
  const rl = readline.createInterface({ input: ctx.stdin, output: ctx.stdout });
  try {
    const ans = (
      await rl.question(
        `An m8t brain App "${slug}" is already installed on ${org}. Keep it (no new App)? [Y/n] `,
      )
    )
      .trim()
      .toLowerCase();
    return ans === "" || ans === "y" || ans === "yes";
  } finally {
    rl.close();
  }
}

/** Poll for an ORG-level installation of this App (all-repos install → one id). */
async function pollOrgInstallation(args: {
  appId: string;
  pem: string;
  org: string;
  onTick?: (n: number) => void;
}): Promise<string> {
  for (let n = 0; n < 150; n++) {
    const jwt = signAppJwt({ appId: args.appId, privateKeyPem: args.pem });
    const res = await fetch("https://api.github.com/app/installations", {
      headers: {
        Authorization: `Bearer ${jwt}`,
        Accept: "application/vnd.github+json",
        "X-GitHub-Api-Version": "2022-11-28",
        "User-Agent": "m8t",
      },
    });
    if (res.ok) {
      const insts = (await res.json()) as { id: number; account?: { login?: string } }[];
      const hit = insts.find(
        (i) => i.account?.login?.toLowerCase() === args.org.toLowerCase(),
      );
      if (hit) return String(hit.id);
    }
    args.onTick?.(n);
    await new Promise((r) => setTimeout(r, 2000));
  }
  throw new LocalCliError({
    code: "GH_APP_INSTALL_TIMEOUT",
    message: `Timed out waiting for the App to be installed on ${args.org}.`,
  });
}

export class BrainAppCreateCommand extends M8tCommand {
  static paths = [["brain", "app-create"]];
  static usage = Command.Usage({
    description:
      "Create + install a per-founder GitHub App (manifest flow) for brain provisioning.",
    details:
      "Opens GitHub's pre-filled 'Create GitHub App' page (no typing), then prompts you to Install it (default: All repositories). Stashes the App id/slug/pem and the org installation id so the cloud installer can create brain repos + mint runtime tokens. Org accounts only — for a personal account, use the gh fallback.",
    examples: [["Create for your org", "$0 brain app-create --org acme"]],
  });

  org = Option.String("--org", { description: "GitHub org to create + install the App in (required)." });
  port = Option.String("--port", "7878");
  pemOut = Option.String("--pem-out");
  credsOut = Option.String("--creds-out");
  output = Option.String("--output");
  keepExisting = Option.Boolean("--keep-existing", false, {
    description: "If a brain App is already installed on the org, reuse it (skip creating a new one).",
  });
  forceNew = Option.Boolean("--new", false, {
    description: "Create a new brain App even if one is already installed on the org.",
  });

  async executeCommand(): Promise<number> {
    if (!this.org) {
      throw new LocalCliError({
        code: "GH_APP_NO_ORG",
        message: "Pass --org <github-org> (the org to create+install the brain App in).",
        hint: "For a personal account, use the gh fallback (m8t brain create without an App).",
      });
    }

    const org = this.org;
    const pemOut =
      typeof this.pemOut === "string"
        ? this.pemOut
        : path.join(os.homedir(), ".m8t", "github-app.pem");
    const credsOut =
      typeof this.credsOut === "string"
        ? this.credsOut
        : path.join(os.homedir(), ".m8t", "github-app.json");
    const port = Number(this.port);
    const onProgress = (m: string) => this.context.stderr.write(`  ${colors.dim(m)}\n`);

    // Idempotency: if a valid brain App is already installed on this org, offer
    // keep-vs-new rather than blindly creating a duplicate.
    const existing = await findValidBrainApp(credsOut, org);
    if (existing) {
      const isTTY = (this.context.stdout as NodeJS.WriteStream).isTTY === true;
      let keep: boolean;
      if (this.keepExisting === true) keep = true;
      else if (this.forceNew === true) keep = false;
      else if (isTTY)
        keep = await promptKeepNew(
          { stdin: this.context.stdin as NodeJS.ReadStream, stdout: this.context.stdout as NodeJS.WriteStream },
          existing.slug,
          org,
        );
      else keep = true; // headless default: idempotent reuse; pass --new to force a fresh App.
      if (keep) {
        this.context.stdout.write(
          `${colors.success("✓")} Reusing the existing brain App ${colors.field(existing.slug)} on ${org} (installation ${existing.installationId}).\n` +
            `  ${colors.dim("Pass --new to create a fresh App instead.")}\n`,
        );
        if (this.output === "json") this.context.stdout.write(JSON.stringify(existing) + "\n");
        return 0;
      }
      onProgress(`creating a NEW brain App on ${org} (existing ${existing.slug} left intact)…`);
    }

    // Unique name suffix: GitHub never frees a deleted App's name, so a fixed
    // m8t-brain-<org> blocks re-create after a delete (and a 2nd env per org).
    const nameSuffix = randomBytes(3).toString("hex");

    this.context.stdout.write(
      `${colors.field("→")} Opening your browser to create + install the GitHub App on ${org}.\n` +
      `  GitHub may ask you to confirm your identity once or twice — a sign-in plus a security re-check for creating an org app. That's expected, not an error.\n` +
      `  ${colors.dim("Follow the page: click Create GitHub App, then Install (leave \"All repositories\"). GitHub brings you right back here when it's done.")}\n`,
    );

    const { app, installationId } = await createGitHubApp({
      org,
      port,
      nameSuffix,
      openBrowser: (u) => { void tryOpenUrl(u); },
      pollInstallation: (a) =>
        pollOrgInstallation({
          appId: a.id,
          pem: a.pem,
          org,
          onTick: (n) => { if (n % 5 === 0) onProgress(`…waiting for install (${(n * 2).toString()}s)`); },
        }),
      onProgress,
    });

    fs.mkdirSync(path.dirname(pemOut), { recursive: true });
    fs.writeFileSync(pemOut, app.pem, { mode: 0o600 });
    this.context.stdout.write(
      `${colors.success("✓")} App created: ${colors.field(app.slug)} (id ${app.id}); pem → ${pemOut}\n` +
      `${colors.success("✓")} installed on ${org} (installation ${installationId})\n`,
    );

    const creds = {
      appId: app.id,
      slug: app.slug,
      org,
      installationId,
      pemPath: pemOut,
    };

    // Persist the creds JSON so `bootstrap launch` can read them without manual redirection.
    fs.mkdirSync(path.dirname(credsOut), { recursive: true });
    fs.writeFileSync(credsOut, JSON.stringify(creds, null, 2), { mode: 0o600 });
    this.context.stdout.write(
      `${colors.success("✓")} creds → ${credsOut}\n`,
    );

    if (this.output === "json") this.context.stdout.write(JSON.stringify(creds) + "\n");
    return 0;
  }
}
